import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { api } from "../../../convex/_generated/api";
import { identifyAnalytics, resetAnalytics } from "../lib/analytics";

// ============================================================================
// Types
// ============================================================================

interface CurrentUser {
  _id: string;
  email?: string;
  isPremium?: boolean;
  planKey?: string;
  planLimit?: number;
  notificationsEnabled: boolean;
  timezone: string;
  digestFrequency: "NONE" | "DAILY" | "WEEKLY";
  createdAt: number;
  appleUserId?: string;
}

interface AuthContextValue {
  /** True while checking session state on mount */
  isLoading: boolean;
  /** True when user has a valid Convex auth session */
  isAuthenticated: boolean;
  /** Full user record from Convex users table */
  user: CurrentUser | null;
  /** Sign out and clear the session */
  signOut: () => Promise<void>;
  /** Create a silent anonymous session for pre-login purchase flows */
  createAnonymousSession: () => Promise<void>;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);
const AUTH_READY_TIMEOUT_MS = 15_000;

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within <AuthProvider>");
  }
  return ctx;
}

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isLoading: convexLoading, isAuthenticated: convexAuthed } =
    useConvexAuth();
  const { signIn: convexSignIn, signOut: convexSignOut } = useAuthActions();
  const authReadyWaiters = useRef<
    {
      resolve: () => void;
      reject: (reason?: unknown) => void;
      timeoutId: ReturnType<typeof setTimeout>;
    }[]
  >([]);
  const authenticatedRef = useRef(convexAuthed);

  useEffect(() => {
    authenticatedRef.current = convexAuthed;
    if (!convexAuthed) return;

    const waiters = authReadyWaiters.current.splice(0);
    for (const waiter of waiters) {
      clearTimeout(waiter.timeoutId);
      waiter.resolve();
    }
  }, [convexAuthed]);

  // Only fetch user when authenticated to avoid unauthenticated query errors
  const rawUser = useQuery(api.users.current, convexAuthed ? {} : "skip");

  const [user, setUser] = useState<CurrentUser | null>(null);

  const authenticatedUserId = convexAuthed ? rawUser?._id : undefined;

  useEffect(() => {
    if (!convexAuthed || rawUser === undefined) {
      return;
    }
    if (rawUser === null) {
      setUser(null);
      return;
    }
    setUser({
      _id: rawUser._id,
      email: rawUser.email,
      isPremium: rawUser.isPremium ?? false,
      planKey: rawUser.planKey ?? "free",
      planLimit: rawUser.planLimit ?? 0,
      notificationsEnabled: rawUser.notificationsEnabled,
      timezone: rawUser.timezone,
      digestFrequency: rawUser.digestFrequency,
      createdAt: rawUser.createdAt,
      appleUserId: rawUser.appleUserId,
    });
  }, [convexAuthed, rawUser]);

  useEffect(() => {
    if (authenticatedUserId) {
      // Convex's stable user ID links web events to backend events without
      // placing an email address or other personal data in analytics.
      identifyAnalytics(authenticatedUserId);
    }
  }, [authenticatedUserId]);

  // Clear user on sign-out
  useEffect(() => {
    if (!convexAuthed && !convexLoading) {
      setUser(null);
      resetAnalytics();
    }
  }, [convexAuthed, convexLoading]);

  const signOut = useCallback(async () => {
    await convexSignOut();
    setUser(null);
    resetAnalytics();
  }, [convexSignOut]);

  const waitForAuthentication = useCallback(() => {
    if (authenticatedRef.current) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const index = authReadyWaiters.current.findIndex(
          (waiter) => waiter.timeoutId === timeoutId,
        );
        if (index >= 0) authReadyWaiters.current.splice(index, 1);
        reject(new Error("Convex authentication did not become ready"));
      }, AUTH_READY_TIMEOUT_MS);

      authReadyWaiters.current.push({ resolve, reject, timeoutId });
    });
  }, []);

  const createAnonymousSession = useCallback(async () => {
    if (authenticatedRef.current) return;
    await convexSignIn("anonymous");
    await waitForAuthentication();
  }, [convexSignIn, waitForAuthentication]);

  const isLoading = convexLoading || (convexAuthed && rawUser === undefined);

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isAuthenticated: convexAuthed,
        user,
        signOut,
        createAnonymousSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
